// Shared pricing mechanics for plan displays. The *copy* around these numbers
// diverges per surface (anonymous signup vs. eligibility-aware in-app), but the
// money formatting, monthly-equivalent math, and offer-shape mapping stay here so
// they can't drift apart.

export type PlanOffer = {
  amount: number;
  duration: "once" | "forever" | "repeating";
  duration_in_months: number | null;
};

export type Plan = {
  id: string;
  name: string;
  description: string | null;
  amount: number;
  currency: string;
  interval: string;
  trial_period_days: number;
  features: string[];
  // Advertisable discount resolved server-side, or null when there's no usable
  // offer to show. See Billing::PlanOffer.
  offer?: PlanOffer | null;
  // True for the plan the signup form pre-selects when it offers a choice.
  default?: boolean;
};

export type OfferMode =
  | "annual"
  | "monthly"
  | "annual_once"
  | "annual_forever"
  | "monthly_once"
  | "monthly_forever"
  | "monthly_repeating";

export function formatMoney(cents: number, currency: string, withCents = cents % 100 !== 0): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: (currency || "usd").toUpperCase(),
    minimumFractionDigits: withCents ? 2 : 0,
    maximumFractionDigits: withCents ? 2 : 0,
  }).format(cents / 100);
}

// Monthly-equivalent of an annual price (annual ÷ 12), for display only — the
// annual total is what actually hits the invoice. Rounds to whole dollars: cents
// is money-format (dollars * 100), so divide by 12 and by 100 to get a decimal
// dollar amount, round that, then multiply back by 100 before handing whole cents
// to formatMoney — avoids spurious cent precision like $83.33. Prefixed with "~"
// only when that rounding actually changes the value (annual not divisible by 12
// whole dollars); an evenly-dividing price shows no tilde.
export function perMonth(cents: number, currency: string): string {
  const isApproximate = cents % 1200 !== 0;
  return `${isApproximate ? "~" : ""}${formatMoney(Math.round(cents / 12 / 100) * 100, currency)}`;
}

// Which pricing shape a plan renders, derived from its billing interval and the
// resolved offer. Copy differs per surface, but this selection is shared.
export function offerMode(plan: Plan): OfferMode {
  const offer = plan.offer;
  const isAnnual = plan.interval === "year";

  if (offer && offer.amount < plan.amount) {
    if (isAnnual && offer.duration === "once") return "annual_once";
    if (isAnnual && offer.duration === "forever") return "annual_forever";
    if (!isAnnual && offer.duration === "once") return "monthly_once";
    if (!isAnnual && offer.duration === "forever") return "monthly_forever";
    if (!isAnnual && offer.duration === "repeating") return "monthly_repeating";
  }
  return isAnnual ? "annual" : "monthly";
}
